import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useRemediation } from '../../context/RemediationContext';

const { FiZap, FiUser, FiTarget, FiAlertTriangle, FiCheck, FiChevronDown } = FiIcons;

const EVENT_TYPES = [
  { value: 'Clicked Link', label: 'Clicked Link', severity: 'medium', color: 'text-orange-600' },
  { value: 'Submitted Credentials', label: 'Submitted Credentials', severity: 'critical', color: 'text-red-600' },
  { value: 'Opened Email', label: 'Opened Email', severity: 'low', color: 'text-yellow-600' },
];

const SIMULATIONS = [
  { id: 'PS001', name: 'Urgent: Password Expiry' },
  { id: 'PS003', name: 'IT: Mandatory Software Update' },
  { id: 'PS_NEW', name: 'Q4 Holiday Phishing Campaign' },
];

const TriggerSimulationPanel = ({ learners }) => {
  const { triggerPhishingFailure } = useRemediation();
  const [form, setForm] = useState({ learnerId: '', simulationId: 'PS001', eventType: 'Clicked Link' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const upd = patch => setForm(f => ({ ...f, ...patch }));

  const handleTrigger = async () => {
    if (!form.learnerId) return;
    setLoading(true);
    setResult(null);
    await new Promise(r => setTimeout(r, 800));
    const sim = SIMULATIONS.find(s => s.id === form.simulationId);
    const rec = triggerPhishingFailure({ ...form, simulationName: sim?.name || form.simulationId });
    setResult(rec);
    setLoading(false);
  };

  const rule = EVENT_TYPES.find(e => e.value === form.eventType);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
          <SafeIcon icon={FiZap} className="text-red-500 text-base" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Trigger Phishing Failure</h3>
          <p className="text-xs text-slate-500">Simulate a phishing event to test the workflow</p>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Select Learner</label>
          <div className="relative">
            <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <select value={form.learnerId} onChange={e => upd({ learnerId: e.target.value })}
              className="w-full pl-8 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:outline-none focus:border-blue-500 appearance-none">
              <option value="">Choose a learner…</option>
              {learners.map(l => (
                <option key={l.id} value={l.id}>{l.firstName} {l.lastName} — Risk: {l.phishingRiskScore}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Simulation</label>
          <div className="relative">
            <SafeIcon icon={FiTarget} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <select value={form.simulationId} onChange={e => upd({ simulationId: e.target.value })}
              className="w-full pl-8 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:outline-none focus:border-blue-500 appearance-none">
              {SIMULATIONS.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Failure Type</label>
          <div className="flex flex-col gap-2">
            {EVENT_TYPES.map(e => (
              <label key={e.value} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${form.eventType === e.value ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                <input type="radio" name="eventType" value={e.value} checked={form.eventType === e.value} onChange={() => upd({ eventType: e.value })} className="accent-blue-600" />
                <span className={`text-xs font-semibold ${e.color}`}>{e.label}</span>
                <span className="text-xs text-slate-400 ml-auto capitalize">{e.severity}</span>
              </label>
            ))}
          </div>
        </div>

        {rule && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1">
            <p>⚠️ Risk score will increase by <strong>+{rule.value === 'Clicked Link' ? 15 : rule.value === 'Submitted Credentials' ? 25 : 5} pts</strong></p>
            <p>📚 Remedial training will be auto-assigned</p>
            {rule.value === 'Submitted Credentials' && <p>🔔 Manager & admin will be notified</p>}
          </div>
        )}
      </div>

      <button onClick={handleTrigger} disabled={!form.learnerId || loading}
        className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors ${form.learnerId && !loading ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}>
        {loading ? (
          <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}>
            <SafeIcon icon={FiZap} />
          </motion.div>
        ) : <SafeIcon icon={FiAlertTriangle} />}
        {loading ? 'Processing…' : 'Trigger Failure & Auto-Assign'}
      </button>

      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold text-xs">
              <SafeIcon icon={FiCheck} />Workflow triggered successfully
            </div>
            <p className="text-xs text-emerald-600">Module assigned · Due: {result.dueDate}</p>
            <p className="text-xs text-emerald-600">Notifications sent: {result.notificationsSent.join(', ')}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TriggerSimulationPanel;